import {
    Component,
    DestroyRef,
    inject,
    input,
    OnInit,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { EMPTY, Observable, forkJoin } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import {
    ComponentsModule,
    FormFields,
    FormStatus,
} from '@jht/brand-components';
import { FormSubmissionModule } from '../../../shared/modules/form-submission/form-submission.module';
import { FormSubmissionService } from '../../../shared/modules/form-submission/form-submission.service';

import { TranslateModule, TranslateService } from '@jht/brand-translate';
import { PipesModule } from '@jht/brand-pipes';

import { ContentService } from '../../../shared/content/content.service';
import { StateCountrySelectorModule } from '../../../shared/modules/state-country-selectors/state-country-selector.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CountrySelectOption } from '@src/app/shared/modules/state-country-selectors/country-selector/model';

@Component({
    selector: 'vision2-request-quote',
    imports: [
        CommonModule,
        ComponentsModule,
        FormSubmissionModule,
        StateCountrySelectorModule,
        PipesModule,
        TranslateModule,
    ],
    templateUrl: './request-quote.component.html',
    styleUrl: './request-quote.component.scss',
})
export class RequestQuoteComponent implements OnInit {
    productData = input<any>(null);

    fields: FormFields;
    formSubmitted = signal(false);
    selectedCountry = signal<CountrySelectOption>({
        recipientCode: '',
        recipient: '',
        value: '',
    });
    errorIconUrl: string;

    private contentService = inject(ContentService);
    private formSubmissionService = inject(FormSubmissionService);
    private destroyRef = inject(DestroyRef);
    private translate = inject(TranslateService);

    constructor() {
        this.onFormSubmit = this.onFormSubmit.bind(this);
    }

    ngOnInit(): void {
        this.onFormInit();
    }

    onFormStatusChange(formState: FormStatus): void {
        if (formState === 'submitted') {
            this.formSubmissionService.sendAnalytics();
            this.formSubmitted.set(true);
        }
    }

    onFormSubmit(formData?: FormData): Observable<any> {
        if (!formData) {
            return EMPTY;
        }

        formData.set('_title', `Vision2 Collection Request a Quote`);
        formData.set('_form_id', `vision2--request-a-quote`);

        const countryName = this.selectedCountry()?.value ?? '';
        const recipientCode = this.selectedCountry()?.recipientCode ?? '';

        formData.set('country', countryName);
        formData.set('source', 'Vision2 Web - Request a Quote');

        // set a product field containing web display name as value
        formData.set('product', this.productData()?.webDisplayName);

        // make this form submit to same recipients as sales and availability topic category in cms component
        const topicCategory = 'Sales and Availability';

        return this.getRecipients(topicCategory, recipientCode).pipe(
            takeUntilDestroyed(this.destroyRef),
            filter((recipient) => !!recipient),
            switchMap((recipient: string) => {
                const newFormData: FormData =
                    this.formSubmissionService.structureFormData(formData);
                newFormData.set('_to', recipient);
                return this.formSubmissionService.submitContactUsForm(
                    newFormData,
                );
            }),
        );
    }

    onFormInit() {
        this.errorIconUrl = this.translate.instant(
            'CONTACT-US_FORM_ERROR_IMAGE_URL',
        );

        this.fields = {
            _confirmation_subject: { value: '' },
            _confirmation_message: { value: '' },
            _gottem: { value: '' },

            // submitter
            firstName: { value: '', required: true },
            lastName: { value: '', required: true },
            email: { value: '', required: true, validator: 'email' },
            phone: {
                value: '',
                validator: 'phone',
                required: true,
            },
            country: { value: '', required: true },
            postalCode: { value: '', required: true },
            location: { value: '', required: true },
        };
    }

    getRecipients(topic: string, countryCode: string): Observable<string> {
        return forkJoin([
            this.contentService.getComponent('contact-us-country-selector'),
            this.contentService.getComponent('contact-us-topic-recipients'),
        ]).pipe(
            filter(([countries, topics]) => !!countries && !!topics),
            map(([countries, topicRecipients]) => {
                if (topicRecipients) {
                    const recipient = topicRecipients.find(
                        (recipient: any) =>
                            recipient?.category === topic &&
                            recipient['recipient-code'] === countryCode,
                    );

                    // set a fallbackRecipient if match not found for country code and topic
                    const fallbackRecipient = topicRecipients.find(
                        (recipient: any) =>
                            recipient?.category === topic &&
                            (recipient['recipient-code'] === 'World' ||
                                recipient['recipient-code'] === 'WORLD'),
                    );

                    // return the recipient string or throw an error if not found
                    if (recipient) {
                        return recipient['email-address'];
                    } else {
                        if (fallbackRecipient) {
                            return fallbackRecipient['email-address'];
                        } else {
                            throw new Error(
                                'Recipient not found. Ensure fallback recipient exists.',
                            );
                        }
                    }
                }
            }),
        );
    }

    onCountryChange(countrySelected: CountrySelectOption): void {
        this.selectedCountry.set(countrySelected);
    }
}
